import React, { useEffect, useState, useRef } from 'react';
import { Play, Pause } from 'lucide-react';
import { playDrum } from '../../utils/audioUtils';
const TEMPO_PRESETS = [{
  name: '慢速',
  bpm: 60,
  description: '像散步一样慢',
  emoji: '🐢'
}, {
  name: '中速',
  bpm: 90,
  description: '像走路一样',
  emoji: '🚶'
}, {
  name: '快速',
  bpm: 120,
  description: '像跑步一样快',
  emoji: '🏃'
}, {
  name: '很快',
  bpm: 150,
  description: '像心跳加速',
  emoji: '💓'
}];
export function TempoExplorer() {
  const [bpm, setBpm] = useState(90);
  const [isPlaying, setIsPlaying] = useState(false);
  const [beatCount, setBeatCount] = useState(0);
  const [pendulumAngle, setPendulumAngle] = useState(0);
  const intervalRef = useRef<number | null>(null);
  useEffect(() => {
    if (isPlaying) {
      const interval = 60 / bpm * 1000;
      let direction = 1;
      intervalRef.current = window.setInterval(() => {
        playDrum('woodblock');
        setBeatCount(prev => prev + 1);
        setPendulumAngle(prev => {
          direction = prev >= 30 ? -1 : prev <= -30 ? 1 : direction;
          return prev + direction * 15;
        });
      }, interval);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      setPendulumAngle(0);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPlaying, bpm]);
  const handlePresetClick = (presetBpm: number) => {
    setBpm(presetBpm);
    setBeatCount(0);
  };
  return <div className="flex flex-col items-center w-full max-w-4xl mx-auto" data-id="element-224">
      {/* Metronome visualization */}
      <div className="bg-white rounded-3xl p-8 w-full max-w-md mb-8 shadow-lg" data-id="element-225">
        <div className="flex justify-center mb-8" data-id="element-226">
          {/* Metronome */}
          <div className="relative" data-id="element-227">
            {/* Body */}
            <div className="w-40 h-52 bg-gradient-to-b from-[#8D6E63] to-[#6D4C41] rounded-t-lg relative" style={{
            clipPath: 'polygon(15% 100%, 85% 100%, 100% 0, 0 0)'
          }} data-id="element-228">
              {/* Face */}
              <div className="absolute top-6 left-1/2 -translate-x-1/2 w-28 h-28 bg-[#EFEBE9] rounded-lg shadow-inner" data-id="element-229">
                {/* Scale marks */}
                <div className="absolute top-4 left-1/2 -translate-x-1/2 flex gap-4" data-id="element-230">
                  {[-2, -1, 0, 1, 2].map(i => <div key={i} className={`w-0.5 ${i === 0 ? 'h-4 bg-gray-600' : 'h-3 bg-gray-400'}`} style={{
                  transform: `rotate(${i * 15}deg)`
                }} data-id="element-231" />)}
                </div>

                {/* Pendulum */}
                <div className="absolute bottom-0 left-1/2 w-1.5 h-20 bg-[#795548] origin-bottom transition-transform" style={{
                transform: `translateX(-50%) rotate(${pendulumAngle}deg)`,
                transitionDuration: isPlaying ? `${30 / bpm}s` : '0.3s'
              }} data-id="element-232">
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-6 h-6 bg-[#FF5722] rounded-full shadow-md" data-id="element-233" />
                </div>
              </div>

              {/* BPM display */}
              <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-center" data-id="element-234">
                <div className="text-3xl font-bold text-white" data-id="element-235">{bpm}</div>
                <div className="text-xs text-white/70" data-id="element-236">BPM</div>
              </div>
            </div>
          </div>
        </div>

        {/* Beat indicator */}
        <div className="flex justify-center gap-2 mb-6" data-id="element-237">
          {[0, 1, 2, 3].map(i => <div key={i} className={`w-4 h-4 rounded-full transition-all ${beatCount % 4 === i && isPlaying ? 'bg-[#FF5722] scale-125' : 'bg-gray-200'}`} data-id="element-238" />)}
        </div>

        {/* Play button */}
        <div className="flex justify-center" data-id="element-239">
          <button onClick={() => setIsPlaying(!isPlaying)} className={`w-16 h-16 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-105 ${isPlaying ? 'bg-gray-200' : 'bg-[#795548]'}`} data-id="element-240">
            {isPlaying ? <Pause className="w-8 h-8 text-gray-700" data-id="element-241" /> : <Play className="w-8 h-8 text-white ml-1" data-id="element-242" />}
          </button>
        </div>
      </div>

      {/* Tempo presets */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 w-full max-w-2xl mb-6" data-id="element-243">
        {TEMPO_PRESETS.map(preset => <button key={preset.name} onClick={() => handlePresetClick(preset.bpm)} className={`p-4 rounded-2xl transition-all ${bpm === preset.bpm ? 'bg-[#795548] text-white shadow-lg scale-105' : 'bg-white text-gray-700 shadow hover:shadow-md'}`} data-id="element-244">
            <div className="text-2xl mb-1" data-id="element-245">{preset.emoji}</div>
            <div className="font-bold" data-id="element-246">{preset.name}</div>
            <div className="text-sm opacity-70" data-id="element-247">{preset.bpm} BPM</div>
          </button>)}
      </div>

      {/* Slider */}
      <div className="w-full max-w-md bg-white rounded-2xl p-6 shadow-lg" data-id="element-248">
        <label className="block text-center mb-4 text-gray-600 font-medium" data-id="element-249">
          拖动调整速度
        </label>
        <input type="range" min="40" max="180" value={bpm} onChange={e => setBpm(Number(e.target.value))} className="w-full h-3 bg-gray-200 rounded-full appearance-none cursor-pointer accent-[#795548]" data-id="element-250" />
        <div className="flex justify-between text-sm text-gray-400 mt-2" data-id="element-251">
          <span data-id="element-252">慢 🐢</span>
          <span data-id="element-253">快 🏃</span>
        </div>
      </div>

      {/* Tips */}
      <div className="mt-6 bg-amber-50 rounded-xl p-4 max-w-md text-center" data-id="element-254">
        <p className="text-amber-800 font-medium" data-id="element-255">🎵 跟着节拍器拍手或跺脚</p>
        <p className="text-amber-600 text-sm mt-1" data-id="element-256">
          试试不同的速度，感受快慢的变化！
        </p>
      </div>
    </div>;
}